import dayjs from 'dayjs';
import {
  takeLatest,
  takeLeading,
  put,
  putResolve,
  select,
  delay,
  all,
} from 'redux-saga/effects';
import { v1 as uuidv1 } from 'uuid';
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  Timestamp,
  addDoc,
  getDocs,
  deleteDoc,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';

import { db, storage } from 'firebaseConfig';

import { projectSuccess } from 'modules/Projects/store/actions';
import { pushNotification } from 'modules/Users/store/actions';
import { pxToNumber } from 'utils';
import {
  actions,
  slideSuccess,
  slidesRequest,
  uploading,
  toggleLoading,
  slideRequest,
  slidesSuccess,
  fetchImageDownloadURLs,
} from './actions';

import { currentProject, currentSlide, slides } from './selectors';

function* addSlideSaga() {
  const timestamp = Timestamp.now();
  const project = yield select(currentProject);
  const projectDocRef = doc(db, 'projects', project.id);
  const newSlide = yield addDoc(collection(projectDocRef, 'slides'), {
    createdAt: timestamp,
  });
  const newSlides = project.slides || [];

  newSlides.push({
    id: newSlide.id,
    createdAt: timestamp,
    noOfComments: 0,
    thumbnail: null,
  });
  yield put(slideSuccess({ id: newSlide.id, createdAt: timestamp }));
  yield put(
    projectSuccess({ ...project, slides: newSlides, modifiedAt: timestamp }),
  );
  yield updateDoc(projectDocRef, {
    slides: newSlides,
    modifiedAt: timestamp,
  });
}

export function* watchAddSlide() {
  yield takeLatest(actions.ADD_SLIDE, addSlideSaga);
}

function* deleteSlideSaga({ payload }) {
  const timestamp = Timestamp.now();
  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const projectRef = doc(db, 'projects', project.id);
  let newSlides = project.slides.filter((i) => i.id !== payload);
  const slideToDelete = project.slides.find((i) => i.id === payload);
  const slideToDeleteIndex = project.slides.findIndex((i) => i.id === payload);
  let newCurrentSlideID = '';

  if (newSlides.length < 1) {
    newSlides = [];
    newSlides.push({
      id: slideToDelete.id,
      noOfComments: 0,
      createdAt: timestamp,
      thumbnail: null,
    });
    yield put(slideSuccess({ id: slideToDelete.id, createdAt: timestamp }));
    yield setDoc(doc(db, 'slides', slideToDelete.id), {
      createdAt: timestamp,
    });
  } else {
    if (slide.id === slideToDelete.id) {
      newCurrentSlideID =
        project.slides[slideToDeleteIndex === 0 ? 0 : slideToDeleteIndex - 1]
          .id;

      yield put(
        slideRequest({ projectID: project.id, slideID: newCurrentSlideID }),
      );
    }

    yield deleteDoc(doc(db, 'slides', slideToDelete.id));
  }

  yield put(projectSuccess({ ...project, slides: newSlides }));
  yield put(slidesSuccess(allSlides.filter((i) => i.id !== payload)));

  yield updateDoc(projectRef, {
    modifiedAt: timestamp,
    slides: newSlides,
  });
}

export function* watchDeleteSlide() {
  yield takeLatest(actions.DELETE_SLIDE, deleteSlideSaga);
}

function* slidesRequestSaga({ payload: { projectID, slideID } }) {
  const slidesColRef = collection(db, 'projects', projectID, 'slides');
  let slidesColSnap = yield getDocs(slidesColRef);
  const slide = yield select(currentSlide);

  slidesColSnap = slidesColSnap.docs.map((i) => ({ id: i.id, ...i.data() }));

  if (slide)
    slidesColSnap = slidesColSnap.map((i) =>
      i.id === slide.id ? { ...i, images: slide.images } : i,
    );

  yield putResolve(slidesSuccess(slidesColSnap));
  yield put(slideRequest({ projectID, slideID }));
  yield put(fetchImageDownloadURLs());
}

export function* watchSlidesRequest() {
  yield takeLatest(actions.SLIDES_REQUEST, slidesRequestSaga);
}

function* slideRequestSaga({ payload: { projectID, slideID } }) {
  const allSlides = yield select(slides);
  const slideDocRef = doc(db, 'projects', projectID, 'slides', slideID);
  let slide = null;
  let reUploadSlides = false;

  if (allSlides.length > 0 && allSlides.some((i) => i.id === slideID)) {
    slide = allSlides.find((i) => i.id === slideID);
  } else {
    yield put(toggleLoading(true));
    yield put(slidesRequest({ projectID, slideID }));

    return;
  }

  if (slide.images && slide.images.length > 0) {
    if (slide.images.some((i) => !i.downloadURL)) {
      yield put(toggleLoading(true));
      slide.images = yield all(
        slide.images
          .filter((i) => Boolean(i.fullPath))
          .map((i) =>
            getDownloadURL(ref(storage, i.fullPath))
              .then((url) => ({ ...i, downloadURL: url }))
              .catch(() => ({ ...i, downloadURL: 'error' })),
          ),
      );

      if (allSlides.length > 0) {
        yield put(
          slidesSuccess(
            allSlides.map((i) =>
              i.id === slide.id ? { ...i, images: slide.images } : i,
            ),
          ),
        );
      }
    }

    slide.images = slide.images.map((i) => {
      if (!i.id) {
        i.id = uuidv1();
        reUploadSlides = true;
      }
      return i;
    });
  }

  yield put(slideSuccess(slide));
  yield put(toggleLoading(false));
  // yield put(listenCommentsUpdates(slideDocRef));

  if (reUploadSlides) {
    yield updateDoc(slideDocRef, {
      images: slide.images.map((i) => {
        const obj = { ...i };
        delete obj.downloadURL;
        return obj;
      }),
    });
  }
}

export function* watchSlideRequest() {
  yield takeLatest(actions.SLIDE_REQUEST, slideRequestSaga);
}

function* fetchImageDownloadURLsSaga() {
  const project = yield select(currentProject);
  let allSlides = yield select(slides);
  let allImages = [];
  const reUploadSlides = [];

  allSlides.forEach((i) => {
    if (i.images && i.images.some((j) => !j.downloadURL))
      allImages.push(i.images);
  });
  allImages = allImages.flat();
  allImages = yield all(
    allImages.map((i) =>
      getDownloadURL(ref(storage, i.fullPath))
        .then((url) => ({ ...i, downloadURL: url }))
        .catch(() => ({ ...i, downloadURL: 'error' })),
    ),
  );
  allSlides = allSlides.map((i) => {
    if (!i.id) {
      i.id = uuidv1();
      reUploadSlides.push(i.id);
    }

    /* eslint-disable indent */
    return i.images
      ? {
          ...i,
          images: i.images.map((j) => allImages.find((a) => a.id === j.id)),
        }
      : i;
    /* eslint-enable indent */
  });

  yield put(slidesSuccess(allSlides));

  if (reUploadSlides.length > 0) {
    yield all(
      reUploadSlides
        .map((i) => allSlides.find((j) => j.id === i))
        .map((i) =>
          updateDoc(doc(db, 'projects', project.id, 'slides', i.id), {
            images: i.images.map((j) => {
              const obj = { ...j };
              delete obj.downloadURL;
              return obj;
            }),
          }),
        ),
    );
  }
}

export function* watchFetchImageDownloadURLs() {
  yield takeLeading(
    actions.FETCH_IMAGE_DOWNLOAD_URLS,
    fetchImageDownloadURLsSaga,
  );
}

function* addImageSaga({ payload }) {
  yield put(uploading(true));

  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const slideDocRef = doc(db, 'projects', project.id, 'slides', slide.id);
  const newImages = slide.images || [];
  const uploadedFile = yield uploadBytes(
    ref(
      storage,
      `illustrations/${project.id}/${
        slide.id
      }/${dayjs().unix()}.${payload.name.substring(
        payload.name.lastIndexOf('.') + 1,
        payload.name.length,
      )}`,
    ),
    payload,
    { cacheControl: 'private,max-age=31536000' },
  );

  const newImage = yield getDownloadURL(
    ref(storage, uploadedFile.metadata.fullPath),
  ).then((url) => ({
    id: uuidv1(),
    fullPath: uploadedFile.metadata.fullPath,
    downloadURL: url,
  }));

  newImages.push(newImage);

  yield put(slideSuccess({ ...slide, images: newImages }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, images: newImages },
    ]),
  );
  yield put(uploading(false));

  yield updateDoc(slideDocRef, {
    images: newImages.map(({ downloadURL, ...rest }) => rest),
  });
  yield updateDoc(doc(db, 'projects', project.id), {
    modifiedAt: Timestamp.now(),
  });
}

export function* watchAddImage() {
  yield takeLatest(actions.ADD_IMAGE, addImageSaga);
}

function* deleteImageSaga({ payload }) {
  const timestamp = Timestamp.now();
  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const slidesColRef = collection(db, 'projects', project.id, 'slides');
  const slideDocRef = doc(slidesColRef, slide.id);
  const imageToRemove = slide.images.find((i) => i.id === payload);
  let newImages = slide.images.filter((i) => i.id !== payload);
  let slidesColSnap = yield getDocs(slidesColRef);
  let imageFound = false;

  yield put(slideSuccess({ ...slide, images: newImages }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, images: newImages },
    ]),
  );

  if (slidesColSnap.docs && slidesColSnap.docs.length > 0) {
    slidesColSnap = slidesColSnap.docs.map((i) => i.data());
    slidesColSnap = slidesColSnap.filter(
      (i) => i.images && i.images.length > 0,
    );
    slidesColSnap.forEach((i) => {
      i.images.forEach((image) => {
        if (image.fullPath === imageToRemove.fullPath && image.id !== payload)
          imageFound = true;
      });
    });
  }

  if (!imageFound) yield deleteObject(ref(storage, imageToRemove.fullPath));

  newImages = newImages.map((i) => {
    const img = { ...i };
    if (img.downloadURL) delete img.downloadURL;
    return img;
  });

  yield updateDoc(slideDocRef, {
    images: newImages,
    modifiedAt: timestamp,
  });
  yield updateDoc(doc(db, 'projects', project.id), {
    modifiedAt: timestamp,
  });
}

export function* watchDeleteImage() {
  yield takeLatest(actions.DELETE_IMAGE, deleteImageSaga);
}

function* setSlideThumbnailSaga({ payload: { thumbnail, slideID } }) {
  const project = yield select(currentProject);
  const newSlides = project.slides.map((s) =>
    s.id === slideID ? { ...s, thumbnail } : s,
  );

  yield put(projectSuccess({ ...project, slides: newSlides }));

  yield updateDoc(doc(db, 'projects', project.id), {
    slides: newSlides,
    thumbnail,
  });
}

export function* watchSetSlideThumbnailSaga() {
  yield takeLatest(actions.SET_SLIDE_THUMBNAIL, setSlideThumbnailSaga);
}

function* updateImageStylesSaga({ payload }) {
  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  let newImages = slide.images.filter((i) => i.id !== payload.id);

  newImages.push(payload);
  yield put(slideSuccess({ ...slide, images: newImages }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, images: newImages },
    ]),
  );
  newImages = newImages.map(({ downloadURL, ...rest }) => rest);

  yield delay(3000);
  yield updateDoc(doc(db, 'projects', project.id, 'slides', slide.id), {
    images: newImages,
  });
}

export function* watchUpdateImageStyles() {
  yield takeLatest(actions.UPDATE_IMAGE_STYLES, updateImageStylesSaga);
}

function* addSlideNotesSaga({ payload }) {
  const timestamp = Timestamp.now();
  const project = yield select(currentProject);
  const allSlides = yield select(slides);
  const slide = yield select(currentSlide);
  const projectDocRef = doc(db, 'projects', project.id);
  const slideDocRef = doc(projectDocRef, 'slides', slide.id);

  yield put(slideSuccess({ notes: payload }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, notes: payload },
    ]),
  );
  yield updateDoc(slideDocRef, { notes: payload });
  yield updateDoc(projectDocRef, { modifiedAt: timestamp });
}

export function* watchAddSlideNotes() {
  yield takeLatest(actions.ADD_SLIDE_NOTES, addSlideNotesSaga);
}

function* saveTextSaga({ payload }) {
  const timestamp = Timestamp.now();
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const project = yield select(currentProject);
  const slideDocRef = doc(db, 'projects', project.id, 'slides', slide.id);
  let newTexts = slide.texts || [];

  if (newTexts.some((i) => i.id === payload.id))
    newTexts = newTexts.map((i) => (i.id === payload.id ? payload : i));
  else newTexts.push(payload);

  yield put(slideSuccess({ ...slide, texts: newTexts }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, texts: newTexts },
    ]),
  );
  yield updateDoc(slideDocRef, {
    texts: newTexts,
  });
  yield updateDoc(doc(db, 'projects', project.id), {
    modifiedAt: timestamp,
  });
}

export function* watchSaveText() {
  yield takeLatest(actions.SAVE_TEXT, saveTextSaga);
}

function* deleteTextSaga({ payload }) {
  const timestamp = Timestamp.now();
  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const slideDocRef = doc(db, 'projects', project.id, 'slides', slide.id);
  const newTexts = slide.texts.filter((i) => i.id !== payload);

  yield put(slideSuccess({ ...slide, texts: newTexts }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, texts: newTexts },
    ]),
  );
  yield updateDoc(slideDocRef, { texts: newTexts });
  yield doc(db, 'projects', project.id).update({
    modifiedAt: timestamp,
  });
}

export function* watchDeleteText() {
  yield takeLatest(actions.DELETE_TEXT, deleteTextSaga);
}

function* addCommentSaga({
  payload: { comment, commentID, userID, dotPosition },
}) {
  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const timestamp = Timestamp.now();
  let comments = [];
  let foundCommentThread = {};
  let noOfComments = 0;
  const commentMentionRegExp = new RegExp('{@(.*?)}', 'g');
  const mentionedUsers = comment.match(commentMentionRegExp) || null;
  const notificationType = ['comment', 'illustration'];
  let commentReplyThreadInitiator = null;
  let commentThreadMembers = [];

  if (mentionedUsers && mentionedUsers.length > 0)
    notificationType.push('mention');

  if (slide.comments && slide.comments.length > 0)
    comments = [...slide.comments];

  comments.forEach((c) => {
    delete c.isNew;
  });

  if (comments.some((c) => c.id === commentID)) {
    foundCommentThread = comments.find((c) => c.id === commentID);
    commentReplyThreadInitiator = foundCommentThread.thread[0].userID;
    foundCommentThread.thread.forEach((i) => {
      commentThreadMembers = [
        ...commentThreadMembers,
        ...(i.comment.match(commentMentionRegExp) || []),
      ];
    });
    foundCommentThread.thread.push({
      createdAt: timestamp,
      userID,
      comment,
    });

    const foundCommentThreadIndex = comments.findIndex(
      (i) => i.id === foundCommentThread.id,
    );
    comments[foundCommentThreadIndex] = foundCommentThread;
    notificationType.push('reply');
  } else
    comments.push({
      id: commentID,
      isResolved: false,
      dotPosition,
      thread: [{ createdAt: timestamp, userID, comment }],
    });

  noOfComments = comments.length;

  project.slides = project.slides.map((s) => ({
    createdAt: s.createdAt,
    id: s.id,
    noOfComments: s.noOfComments,
    thumbnail: s.thumbnail,
  }));
  project.slides = project.slides.map((s) =>
    s.id === slide.id ? { ...s, noOfComments } : s,
  );

  yield put(slideSuccess({ ...slide, comments }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, comments },
    ]),
  );
  yield delay(1000);
  yield updateDoc(doc(db, 'projects', project.id, 'slides', slide.id), {
    comments,
  });
  yield updateDoc(doc(db, 'projects', project.id), {
    modifiedAt: timestamp,
    slides: project.slides,
  });

  // Push notifications.

  yield put(
    pushNotification({
      type: notificationType,
      context: {
        commentID,
        projectID: project.id,
      },
      mentionedUsers,
      commentReplyThreadInitiator,
      commentThreadMembers,
    }),
  );
}

export function* watchAddComment() {
  yield takeLatest(actions.ADD_COMMENT, addCommentSaga);
}

function* resolveCommentSaga({
  payload: { isResolved, projectID, commentID },
}) {
  const timestamp = Timestamp.now();
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const slideDocRef = doc(db, 'projects', projectID, 'slides', slide.id);

  const newComments = slide.comments.map((c) =>
    c.id === commentID ? { ...c, isResolved } : c,
  );

  yield put(
    slideSuccess({
      ...slide,
      comments: newComments,
      modifiedAt: timestamp,
    }),
  );
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      {
        ...slide,
        comments: newComments,
        modifiedAt: timestamp,
      },
    ]),
  );

  yield updateDoc(slideDocRef, {
    comments: newComments,
    modifiedAt: timestamp,
  });

  yield updateDoc(doc(db, 'projects', projectID), {
    modifiedAt: timestamp,
  });
}

export function* watchResolveComment() {
  yield takeLatest(actions.RESOLVE_COMMENT, resolveCommentSaga);
}

function* editCommentSaga({ payload: { cid, uid, createdAt, newComment } }) {
  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const slideDocRef = doc(db, 'projects', project.id, 'slides', slide.id);
  const slideDocSnap = yield getDoc(slideDocRef);
  let newComments = slideDocSnap.get('comments');

  newComments = newComments.map((c) => {
    if (c.id === cid) {
      let newThread = c.thread;
      newThread = newThread.map((t) =>
        t.userID === uid && t.createdAt.valueOf() === createdAt.valueOf()
          ? { ...t, comment: newComment }
          : t,
      );
      return { ...c, thread: newThread };
    }
    return c;
  });

  yield put(
    slideSuccess({
      ...slide,
      comments: newComments,
    }),
  );
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      {
        ...slide,
        comments: newComments,
      },
    ]),
  );

  yield updateDoc(slideDocRef, {
    comments: newComments,
  });
}

export function* watchEditComment() {
  yield takeLatest(actions.EDIT_COMMENT, editCommentSaga);
}

function* deleteCommentSaga({ payload: { cid, uid, createdAt } }) {
  const timestamp = Timestamp.now();
  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const currentProjectDocRef = doc(db, 'projects', project.id);
  const currentSlideDocRef = doc(currentProjectDocRef, 'slides', slide.id);
  const currentSlideDocSnap = yield getDoc(currentSlideDocRef);
  let newComments = currentSlideDocSnap.get('comments');

  newComments = newComments.map((c) => {
    if (c.id === cid) {
      let newThread = c.thread;
      newThread = newThread.filter(
        (t) =>
          !(t.userID === uid && t.createdAt.valueOf() === createdAt.valueOf()),
      );
      return { ...c, thread: newThread };
    }
    return c;
  });
  newComments = newComments.filter((c) => c.thread.length > 0);

  yield put(
    slideSuccess({
      ...slide,
      comments: newComments,
      modifiedAt: timestamp,
    }),
  );
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      {
        ...slide,
        comments: newComments,
        modifiedAt: timestamp,
      },
    ]),
  );

  yield updateDoc(currentSlideDocRef, {
    comments: newComments,
    modifiedAt: timestamp,
  });

  yield updateDoc(currentProjectDocRef, { modifiedAt: timestamp });
}

export function* watchDeleteComment() {
  yield takeLatest(actions.DELETE_COMMENT, deleteCommentSaga);
}

function* updateCommentDotPositionSaga({
  payload: { projectID, slideID, commentID, top, left },
}) {
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const slideRef = doc(db, 'projects', projectID, 'slides', slideID);
  const slideSnap = yield getDoc(slideRef);
  let newComments = slideSnap.get('comments');

  newComments = newComments.map((c) =>
    c.id === commentID ? { ...c, dotPosition: { top, left } } : c,
  );

  yield put(
    slideSuccess({
      ...slide,
      comments: newComments,
    }),
  );
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      {
        ...slide,
        comments: newComments,
      },
    ]),
  );

  yield delay(3000);
  yield updateDoc(slideRef, { comments: newComments });
}

export function* watchUpdateCommentDotPosition() {
  yield takeLatest(
    actions.UPDATE_COMMENT_DOT_POSITION,
    updateCommentDotPositionSaga,
  );
}

/*
function* listenCommentsUpdatesSaga({ payload }) {
  const channel = yield call(createDocumentChangeChannel, payload);

  while (true) {
    const changeSnap = yield take(channel);
    const slide = yield select(currentSlide);
    // bad fix
    if (slide.comments && slide.comments.length > 0) {
      const oldComments = [...slide.comments];
      const change = difference(changeSnap.comments, oldComments);

      if (change.length > 0) {
        change.forEach((comment, index) => {
          if (comment) {
            oldComments[index] = { ...changeSnap.comments[index], isNew: true };
          }
        });
      }

      yield put(
        slideSuccess({
          ...slide,
          comments: oldComments,
        }),
      );
    }
  }
}

export function* watchListenCommentsUpdates() {
  yield takeLatest(actions.LISTEN_COMMENTS_UPDATES, listenCommentsUpdatesSaga);
}

function* createDocumentChangeChannel(reference) {
  return eventChannel((emitter) => {
    const unsubscribe = reference.onSnapshot((snapshot) => {
      emitter({
        id: snapshot.id,
        ...snapshot.data(),
      });
    });

    return () => unsubscribe();
  });
}

function difference(object, base) {
  function changes(object, base) {
    return _.transform(object, function(result, value, key) {
      if (!_.isEqual(value, base[key])) {
        result[key] =
          _.isObject(value) && _.isObject(base[key])
            ? changes(value, base[key])
            : value;
      }
    });
  }
  return changes(object, base);
}
 */

function* updateTextPositionSaga({ payload: { id, left, top } }) {
  yield delay(3000);

  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const slideRef = doc(db, 'projects', project.id, 'slides', slide.id);
  const newTexts = slide.texts.map((i) =>
    i.id === id ? { ...i, left, top } : i,
  );

  yield put(slideSuccess({ ...slide, texts: newTexts }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, texts: newTexts },
    ]),
  );
  yield updateDoc(slideRef, { texts: newTexts });
}

export function* watchUpdateTextPosition() {
  yield takeLatest(actions.UPDATE_TEXT_POSITION, updateTextPositionSaga);
}

function* updateTextStylesSaga({ payload: { id, value, style } }) {
  yield delay(3000);

  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const slideRef = doc(db, 'projects', project.id, 'slides', slide.id);
  const newTexts = slide.texts.map((i) =>
    i.id === id ? { ...i, [style]: value } : i,
  );

  yield put(slideSuccess({ ...slide, texts: newTexts }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, texts: newTexts },
    ]),
  );
  yield updateDoc(slideRef, { texts: newTexts });
}

export function* watchUpdateTextStyles() {
  yield takeLatest(actions.UPDATE_TEXT_STYLES, updateTextStylesSaga);
}

function* updateImageContainerDimensionsSaga({ payload }) {
  const { id, width, height, top, left, marginTop, marginLeft } = payload;
  const project = yield select(currentProject);
  const slide = yield select(currentSlide);
  const allSlides = yield select(slides);
  const slideDocRef = doc(db, 'projects', project.id, 'slides', slide.id);

  /* eslint-disable indent */
  let newImages = slide.images.map((i) =>
    i.id === id
      ? {
          ...i,
          top,
          left,
          imageContainer: { width, height, marginTop, marginLeft },
        }
      : i,
  );
  /* eslint-enable indent */

  yield put(slideSuccess({ ...slide, images: newImages }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, images: newImages },
    ]),
  );
  newImages = newImages.map(({ downloadURL, ...rest }) => rest);

  yield delay(3000);
  yield updateDoc(slideDocRef, { images: newImages });
}

export function* watchUpdateImageContainerDimensions() {
  yield takeLatest(
    actions.UPDATE_IMAGE_CONTAINER_DIMENSIONS,
    updateImageContainerDimensionsSaga,
  );
}

function* clipboardPasteImageSaga({ payload }) {
  const timestamp = Timestamp.now();
  const project = yield select(currentProject);
  const allSlides = yield select(slides);
  const slide = yield select(currentSlide);
  const projectDocRef = doc(db, 'projects', project.id);
  const slideDocRef = doc(projectDocRef, 'slides', slide.id);
  const newImages = [...slide.images];
  const newImage = { ...payload };

  if (newImage.top) newImage.top = `${pxToNumber(newImage.top) + 20}px`;
  if (newImage.left) newImage.left = `${pxToNumber(newImage.left) + 50}px`;
  newImage.id = uuidv1();
  newImages.push(newImage);

  yield put(slideSuccess({ ...slide, images: newImages }));
  yield put(
    slidesSuccess([
      ...allSlides.filter((i) => i.id !== slide.id),
      { ...slide, images: newImages },
    ]),
  );

  yield updateDoc(slideDocRef, {
    images: newImages.map((i) => {
      const obj = { ...i };
      delete obj.downloadURL;
      return obj;
    }),
    modifiedAt: timestamp,
  });
  yield updateDoc(projectDocRef, {
    modifiedAt: timestamp,
  });
}

export function* watchClipboardPasteImage() {
  yield takeLatest(actions.CLIPBOARD_PASTE_IMAGE, clipboardPasteImageSaga);
}
